import _ from "lodash";

import { presentations as presentationsApi } from "apis-v2/callables";
import { WorkspaceResourceType } from "common/interfaces";
import { THEME_ATTRIBUTES_MAPPING, getThemeFromPresentationModel } from "common/themes";
import { isRenderer } from "js/config";
import WorkspaceController, { getWorkspaceResources, getWorkspaceResourcesWithDocuments } from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { SharedTheme } from "js/core/models/sharedTheme";
import { Theme } from "js/core/models/theme";
import { app } from "js/namespaces";

const logger = getLogger(LogGroup.THEMES);

class ThemeManager {
    constructor() {
        this._sharedThemesCache = {};
        this._userThemesCache = {};
    }

    async _getSharedThemeCached(themeId) {
        if (!this._sharedThemesCache[themeId]) {
            this._sharedThemesCache[themeId] = (async () => {
                const theme = new SharedTheme({ id: themeId }, { autoLoad: false });
                await theme.load();
                return theme;
            })();
        }
        return this._sharedThemesCache[themeId];
    }

    async _getUserThemeCached(themeId) {
        if (!this._userThemesCache[themeId]) {
            this._userThemesCache[themeId] = (async () => {
                const theme = new Theme({ id: themeId }, { autoLoad: false });
                await theme.load();
                return theme;
            })();
        }
        return this._userThemesCache[themeId];
    }

    async _getThemeResources(themeIds) {
        return await getWorkspaceResources({
            workspaceId: WorkspaceController.workspaceId,
            resourceTypes: [WorkspaceResourceType.THEME],
            resourceFilters: [{ field: "resourceId", operator: "in", value: themeIds }]
        });
    }

    async updateThemeName(themeId, name) {
        return await WorkspaceController.updateWorkspaceResource({
            resourceId: themeId,
            resourceType: WorkspaceResourceType.THEME,
            metadata: { name }
        });
    }

    async updateThemeIsPublished(themeId, isPublished) {
        return await WorkspaceController.updateWorkspaceResource({
            resourceId: themeId,
            resourceType: WorkspaceResourceType.THEME,
            metadata: { isPublished }
        });
    }

    // Most callers use themes for preview/selection and should not persist load-time migrations.
    async getUserTheme(themeId, readonly = true) {
        const theme = await this._getUserThemeCached(themeId);
        if (!readonly) {
            return theme;
        }
        return new Theme(_.cloneDeep(theme.attributes), { disconnected: true, autoLoad: false });
    }

    async getSharedTheme(themeId, readonly = true) {
        const theme = await this._getSharedThemeCached(themeId);
        if (!readonly) {
            return theme;
        }
        return new SharedTheme(_.cloneDeep(theme.attributes), { disconnected: true, autoLoad: false });
    }

    async getThemeResource(themeId) {
        const [resource] = await this._getThemeResources([themeId]);
        return resource;
    }

    async getDefaultSharedTheme(readonly = true) {
        const { items } = await getWorkspaceResourcesWithDocuments({
            workspaceId: WorkspaceController.workspaceId,
            resourceTypes: [WorkspaceResourceType.THEME],
            filters: [{
                field: "resource.metadata.isDefault",
                operator: "eq",
                value: true
            }, {
                field: "resource.metadata.isShared",
                operator: "eq",
                value: true
            }, {
                field: "resource.metadata.isPublished",
                operator: "eq",
                value: true
            }],
            size: 1,
            offset: 0
        });
        if (items.length > 0) {
            return this.getSharedTheme(items[0].resource.resourceId, readonly);
        }

        return this.getFirstPublishedSharedTheme(readonly);
    }

    async getFirstPublishedSharedTheme(readonly = true) {
        const { items } = await getWorkspaceResourcesWithDocuments({
            workspaceId: WorkspaceController.workspaceId,
            resourceTypes: [WorkspaceResourceType.THEME],
            filters: [{
                field: "resource.metadata.isShared",
                operator: "eq",
                value: true
            }, {
                field: "resource.metadata.isPublished",
                operator: "eq",
                value: true
            }],
            sort: [{
                field: "resource.modifiedAt",
                direction: "desc"
            }],
            size: 1,
            offset: 0
        });

        if (items.length === 0) {
            return null;
        }

        return this.getSharedTheme(items[0].resource.resourceId, readonly);
    }

    async getNewestUserTheme(readonly = true) {
        const { items } = await getWorkspaceResourcesWithDocuments({
            workspaceId: WorkspaceController.workspaceId,
            resourceTypes: [WorkspaceResourceType.THEME],
            filters: [{
                field: "resource.metadata.isShared",
                operator: "eq",
                value: false
            }],
            sort: [{
                field: "resource.modifiedAt",
                direction: "desc"
            }],
            size: 1,
            offset: 0
        });
        if (items.length === 0) {
            return null;
        }
        return this.getUserTheme(items[0].resource.resourceId, readonly);
    }

    async getSharedThemesNames() {
        const { items } = await getWorkspaceResourcesWithDocuments({
            workspaceId: WorkspaceController.workspaceId,
            resourceTypes: [WorkspaceResourceType.THEME],
            filters: [{
                field: "resource.metadata.isShared",
                operator: "eq",
                value: true
            }, {
                field: "resource.metadata.isPublished",
                operator: "eq",
                value: true
            }],
            size: 100,
            offset: 0,
            sort: [{
                field: "resource.modifiedAt",
                direction: "desc"
            }],
        });
        return items.map(item => item.resource.metadata.name);
    }

    async getUserThemesNames() {
        const { items } = await getWorkspaceResourcesWithDocuments({
            workspaceId: WorkspaceController.workspaceId,
            resourceTypes: [WorkspaceResourceType.THEME],
            filters: [{
                field: "resource.metadata.isShared",
                operator: "eq",
                value: false
            }, {
                field: "resource.metadata.isPublished",
                operator: "eq",
                value: true
            }],
            size: 100,
            offset: 0,
            sort: [{
                field: "resource.modifiedAt",
                direction: "desc"
            }]
        });
        return items.map(item => item.resource.metadata.name);
    }

    async loadTheme(presentation) {
        const theme = await this.loadThemeFromPresentation(presentation);

        if (!theme.has("colors")) {
            theme.set("colors", theme.getColors());
        }
        if (!theme.has("iconStyle")) {
            theme.set("iconStyle", "chunky");
        }

        await theme.loadTheme();

        if (!presentation.has("theme_colors") && !presentation.has("sharedThemeId") && presentation.has("themeId")) {
            logger.info("Migrating theme into presentation", { presentationId: presentation.id });

            // Copy properties from userTheme
            const defaultTheme = await this.getTheme(presentation.get("themeId"));
            // Migrate old theme
            switch (defaultTheme.get("styleWeight")) {
                case "thin":
                    defaultTheme.set({
                        styleElementStyle: 0,
                        styleFontWeight: "light"
                    });
                    break;
                case "medium":
                    defaultTheme.set({
                        styleElementStyle: 1,
                        styleFontWeight: "light"
                    });
                    break;
                case "heavy":
                    defaultTheme.set({
                        styleElementStyle: 2,
                        styleFontWeight: "heavy"
                    });
                    break;
            }

            // Merge theme with any existing presentation properties
            Object.keys(defaultTheme.attributes).forEach(key => {
                if (!theme.has(key)) {
                    theme.set(key, defaultTheme.get(key));
                }
            });

            this.saveThemeToPresentation(theme, presentation, { internalChange: true });
        }

        if (theme.hasMigrationChanges) {
            // Won't be migrating the presentation model from renderer or player
            if (!isRenderer && !window.isPlayer && !presentation.has("sharedThemeId")) {
                this.saveThemeToPresentation(theme, presentation, { internalChange: true });
            }
        }

        app.currentTheme = theme;
        return theme;
    }

    getPresentationUpdates(theme) {
        const timestamp = new Date().getTime();

        const updates = {
            firstSlideModifiedAt: timestamp
        };

        THEME_ATTRIBUTES_MAPPING.forEach(keys => {
            const presentationKey = keys[0];
            const themeKey = keys[1];
            updates[presentationKey] = theme.attributes[themeKey] != undefined ? theme.attributes[themeKey] : null;
        });

        return updates;
    }

    async saveThemeToPresentation(theme, presentation, presentationUpdateOptions = {}, resetSlideBackgrounds = false) {
        const updates = {
            ...this.getPresentationUpdates(theme),
            sharedThemeId: null
        };

        if (!isRenderer && !window.isPlayer && !presentation.disconnected) {
            await presentation.reload();
        }

        presentation.update(updates, { replaceKeys: true, unsetSilent: true, ...presentationUpdateOptions });

        if (!isRenderer && !window.isPlayer && !presentation.disconnected) {
            await presentation.updatePromise;
            // Requesting server to update modifiedAt values on slides
            await presentationsApi.updateSlidesMetadata({ id: presentation.id, resetSlideBackgrounds });
        }
    }

    async saveSharedThemeToPresentation(theme, presentation, presentationUpdateOptions = {}, resetSlideBackgrounds = false) {
        const timestamp = new Date().getTime();

        const updates = {
            firstSlideModifiedAt: timestamp,
            sharedThemeId: theme.id,
            themeId: null
        };

        THEME_ATTRIBUTES_MAPPING.forEach(keys => {
            const presentationKey = keys[0];
            updates[presentationKey] = null;
        });

        presentation.update(updates, { replaceKeys: true, unsetSilent: true, ...presentationUpdateOptions });

        if (!presentation.disconnected) {
            await presentation.updatePromise;
            // Requesting server to update modifiedAt values on slides
            await presentationsApi.updateSlidesMetadata({ id: presentation.id, resetSlideBackgrounds });
        }
    }

    async loadThemeFromPresentation(presentation) {
        if (presentation.has("sharedThemeId")) {
            const sharedThemeId = presentation.get("sharedThemeId");
            const theme = new SharedTheme({ id: sharedThemeId });
            try {
                await theme.load();
            } catch (err) {
                // 404/403: theme gone or no access — flag so editor can prompt switch-theme. otherwise throw.
                logger.error(err, `[SharedTheme] Could not load shared theme with ID ${sharedThemeId}, presentation ID ${presentation.id}`);
                if (err.status === 404 || err.status === 403) {
                    presentation.set("errorLoadingSharedTheme", true);
                } else {
                    throw err;
                }
            }

            if (theme.isLoaded) {
                // Ensure adapter is detached so any migration changes are not saved
                theme.detachAdapter();
                return theme;
            }
        }

        const theme = getThemeFromPresentationModel(presentation.attributes);
        return new Theme(theme, { disconnected: true, autoLoad: false });
    }

    themeChanged(presentation) {
        for (let i = 0; i < THEME_ATTRIBUTES_MAPPING.length; i++) {
            if (presentation.hasChanged(THEME_ATTRIBUTES_MAPPING[i][0])) {
                return true;
            }
        }
        if (presentation.hasChanged("sharedThemeId")) {
            return true;
        }
        return false;
    }

    async createTeamTheme(themeAttributes, name, isPublished = true) {
        const theme = {
            ..._.cloneDeep(_.omit(themeAttributes, "hidden", "name", "isBuiltIn", "orgId", "sharedThemeId", "isDefault", "id")),
            userId: app.user.id,
            isBuiltIn: false,
            hidden: false,
            name,
        };
        const newTheme = new SharedTheme(theme, { autoSync: false });
        await newTheme.load();

        const resource = {
            resourceId: newTheme.id,
            resourceType: WorkspaceResourceType.THEME,
            tags: [],
            folderIds: [],
            metadata: {
                name,
                isDefault: false,
                isShared: true,
                isPublished,
            }
        };

        await WorkspaceController.createWorkspaceResource({ resource, defaultPermissions: "shared" });

        await newTheme.reload();

        return newTheme;
    }

    async createUserTheme(themeAttributes, name) {
        const theme = {
            ..._.cloneDeep(_.omit(themeAttributes, "hidden", "name", "isBuiltIn", "orgId", "sharedThemeId", "isDefault", "id")),
            userId: app.user.id,
            isBuiltIn: false,
            name,
            hidden: false,
        };

        const newTheme = new Theme(theme, { autoSync: false });
        await newTheme.load();

        const resource = {
            resourceId: newTheme.id,
            resourceType: WorkspaceResourceType.THEME,
            tags: [],
            folderIds: [],
            metadata: {
                name,
                isDefault: false,
                isShared: false,
                isPublished: !theme.hidden,
            }
        };

        await WorkspaceController.createWorkspaceResource({ resource, defaultPermissions: "owned" });

        await newTheme.reload();

        return newTheme;
    }

    deleteTheme(themeId) {
        return WorkspaceController.deleteWorkspaceResource({
            resourceId: themeId,
            resourceType: WorkspaceResourceType.THEME
        });
    }

    async switchTheme(theme, resetSlideBackgrounds) {
        await theme.loadTheme();

        app.currentTheme = theme;

        // reset glyphCache
        Object.keys(app.glyphCache).map(key => {
            app.glyphCache[key] = {};
        });

        if (ds.selection.presentation) {
            if (theme instanceof SharedTheme) {
                await this.saveSharedThemeToPresentation(theme, ds.selection.presentation, {}, resetSlideBackgrounds);
            } else {
                await this.saveThemeToPresentation(theme, ds.selection.presentation, {}, resetSlideBackgrounds);
            }
        }
    }
}

const themeManager = new ThemeManager();

app.themeManager = themeManager;

export default themeManager;
